import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { GenericPageLayout } from 'layouts/genericPage.layout';
import { Button, Loading } from 'carbon-components-react';
import { RootState } from 'redux/root.reducer';
import { CFCProps, MAP_DATA_SERVICE_DATA_SET, SCREEN_SIZE } from 'types';
import SearchPanel from './components/AddressSearch';
import MapView from './components/Map';
import ViewEmergencyContacts from './components/ViewEmergencyContacts';
import {
  fetchEmergencyContactsStart,
  getECGeocodeStart
} from 'redux/emergencyContacts/actions';
import { clearECData } from 'redux/emergencyContacts/actions';
import { useComponentMount } from 'utilities/hooks.util';

const mapStateToProps = ({ emergencyContacts, session }: RootState) => ({
  locCoordinate: emergencyContacts?.GeoJSONPoints,
  loading: emergencyContacts?.ecLoading,
  ecOrganisations: emergencyContacts?.contacts?.Organisations,
  selectedAddress: emergencyContacts?.geocode?.formattedaddress,
  screenSize: session.screenSize
});

const connector = connect(mapStateToProps);
type Props = CFCProps<typeof connector> & any;

export const EmergencyContacts: React.FC<Props> = (props: Props) => {
  const {
    locCoordinate,
    loading,
    ecOrganisations,
    selectedAddress,
    screenSize
  } = props;
  const dispatch: any = useDispatch();
  const [showEC, setShowEC] = useState<boolean>(false);
  const [disableShowECButton, setDisableShowECButton] = useState<boolean>(true);

  useComponentMount(() => {
    return () => {
      dispatch(clearECData());
    };
  });

  const handleShowEC = () => {
    ecOrganisations &&
      Object.keys(ecOrganisations).length > 0 &&
      setShowEC(true);
  };

  useEffect(() => {
    if (locCoordinate) {
      dispatch(fetchEmergencyContactsStart(locCoordinate));
      dispatch(
        getECGeocodeStart({
          latitude: locCoordinate.split(',')[1].trim(),
          longitude: locCoordinate.split(',')[0].trim(),
          datasets: MAP_DATA_SERVICE_DATA_SET.GEOCODEAU
        })
      );
    }
  }, [dispatch, locCoordinate]);

  useEffect(() => {
    if (ecOrganisations) {
      Object.keys(ecOrganisations).length > 0 && setDisableShowECButton(false);
    } else {
      setDisableShowECButton(true);
    }
  }, [disableShowECButton, ecOrganisations]);

  return (
    <div className="emergency-contact--wrapper" data-testid="emergency">
      <GenericPageLayout>
        <div className="emergency-contact--body-top">
          {loading && (
            <Loading
              description="Active loading indicator"
              withOverlay={true}
            />
          )}
          {!showEC ? (
            <>
              <h2 className="heading--top">Emergency Contacts</h2>
              <div id="search-page" className="emergency-contact--search-page">
                <div className="emergency-contact--address-search">
                  <p className="heading-description">
                    First find your location using the map or search
                  </p>
                  <div className="search-address">
                    <SearchPanel
                      defaultValue={selectedAddress}
                      placeHolder="Address, postcode or suburb"
                    />
                  </div>

                  {[
                    SCREEN_SIZE.LG,
                    SCREEN_SIZE.MAX,
                    SCREEN_SIZE.XL,
                    SCREEN_SIZE.MD
                  ].includes(screenSize) && (
                    <div className="show-ec-button">
                      <Button
                        kind="primary"
                        disabled={disableShowECButton}
                        onClick={handleShowEC}
                      >
                        Show emergency contacts
                      </Button>
                      <p>Within a 200m radius of your search</p>
                    </div>
                  )}
                </div>
                <div className="emergency-contact--map-view">
                  <MapView locCoord={locCoordinate} />
                </div>
                {[SCREEN_SIZE.SM].includes(screenSize) && (
                  <div className="show-ec-button-sm">
                    <Button
                      kind="primary"
                      disabled={disableShowECButton}
                      onClick={handleShowEC}
                    >
                      Show emergency contacts
                    </Button>
                    <p>Within a 200m radius of your search</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div id="result-page" className="emergency-contact--result-page">
              <ViewEmergencyContacts
                ecOrganisations={ecOrganisations}
                setShowEC={setShowEC}
                selectedAddress={selectedAddress}
                screenSize={screenSize}
              />
            </div>
          )}
        </div>
      </GenericPageLayout>
    </div>
  );
};

EmergencyContacts.displayName = 'EmergencyContacts';

export default connector(EmergencyContacts);
